import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "../contexts/AuthContext";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { FileText, Download, CheckCircle, Clock, Plus, Trash2, UserPlus } from "lucide-react";
import { toast } from "sonner";

const MONTHS_ES = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];
const formatCurrency = (v) => new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(v);
const currentYear = new Date().getFullYear();
const currentMonth = new Date().getMonth() + 1;

export default function InvoicesPage() {
  const [invoices, setInvoices] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [filterYear, setFilterYear] = useState(String(currentYear));
  const [filterMonth, setFilterMonth] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [genMonth, setGenMonth] = useState(String(currentMonth));
  const [genYear, setGenYear] = useState(String(currentYear));
  const [partialOpen, setPartialOpen] = useState(false);
  const [partialForm, setPartialForm] = useState({ tenant_id: "", month: String(currentMonth), year: String(currentYear), entry_day: "" });
  const [generatingPartial, setGeneratingPartial] = useState(false);

  const load = useCallback(async () => {
    try {
      let params = {};
      if (filterYear !== "all") params.year = parseInt(filterYear);
      if (filterMonth !== "all") params.month = parseInt(filterMonth);
      if (filterStatus !== "all") params.status = filterStatus;
      const [invRes, tenRes] = await Promise.all([
        axios.get(`${API}/invoices`, { params }),
        axios.get(`${API}/tenants`),
      ]);
      setInvoices(invRes.data);
      setTenants(tenRes.data.filter(t => t.active));
    } catch (e) { if (e.response?.status !== 401) toast.error("Error cargando facturas"); }
    finally { setLoading(false); }
  }, [filterYear, filterMonth, filterStatus]);

  useEffect(() => { load(); }, [load]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const { data } = await axios.post(`${API}/invoices/generate`, { month: parseInt(genMonth), year: parseInt(genYear) });
      if (data.created === 0) {
        toast.info("No se generaron facturas nuevas (ya existen o no hay inquilinos activos)");
      } else {
        toast.success(`${data.created} factura(s) generada(s)`);
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error generando facturas");
    } finally { setGenerating(false); }
  };

  const handleDownloadPdf = async (id, number) => {
    try {
      const response = await axios.get(`${API}/invoices/${id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Factura_${number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Error descargando PDF"); }
  };

  const handleToggleStatus = async (inv) => {
    const newStatus = inv.status === "pagada" ? "pendiente" : "pagada";
    try {
      await axios.put(`${API}/invoices/${inv.id}/status`, { status: newStatus });
      toast.success(`Factura marcada como ${newStatus}`);
      load();
    } catch { toast.error("Error actualizando estado"); }
  };

  const handleDelete = async (inv) => {
    if (!window.confirm(`¿Eliminar la factura ${inv.invoice_number}? Esta acción no se puede deshacer.`)) return;
    try {
      await axios.delete(`${API}/invoices/${inv.id}`);
      toast.success(`Factura ${inv.invoice_number} eliminada`);
      load();
    } catch { toast.error("Error eliminando factura"); }
  };

  const handleGeneratePartial = async () => {
    if (!partialForm.tenant_id || !partialForm.entry_day) {
      toast.error("Selecciona inquilino y dia de entrada");
      return;
    }
    setGeneratingPartial(true);
    try {
      const { data } = await axios.post(`${API}/invoices/generate-partial`, {
        tenant_id: partialForm.tenant_id,
        month: parseInt(partialForm.month),
        year: parseInt(partialForm.year),
        entry_day: parseInt(partialForm.entry_day),
      });
      toast.success(`Factura parcial ${data.invoice_number} generada (${data.remaining_days} dias)`);
      setPartialOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error generando factura parcial");
    } finally { setGeneratingPartial(false); }
  };

  const years = Array.from({ length: 10 }, (_, i) => String(currentYear - 5 + i));

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-400">Cargando...</div>;

  return (
    <div data-testid="invoices-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-2xl sm:text-3xl tracking-tight font-bold text-gray-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Facturas</h2>
          <p className="text-sm text-gray-500 mt-1">{invoices.length} facturas encontradas</p>
        </div>
      </div>

      {/* Generate section */}
      <div className="bg-white border border-gray-200 rounded-sm p-6 mb-6" data-testid="generate-section">
        <h3 className="text-sm font-bold tracking-tight text-gray-900 mb-4 uppercase tracking-[0.15em]">Generar facturas mensuales</h3>
        <div className="flex items-end gap-4">
          <div>
            <Label className="text-xs font-semibold text-gray-500 mb-1 block uppercase tracking-wider">Mes</Label>
            <Select value={genMonth} onValueChange={setGenMonth}>
              <SelectTrigger className="w-40 rounded-sm" data-testid="gen-month-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MONTHS_ES.map((m, i) => <SelectItem key={i+1} value={String(i+1)}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-semibold text-gray-500 mb-1 block uppercase tracking-wider">Año</Label>
            <Select value={genYear} onValueChange={setGenYear}>
              <SelectTrigger className="w-28 rounded-sm" data-testid="gen-year-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {years.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <button
            onClick={handleGenerate}
            disabled={generating}
            className="bg-[#002FA7] text-white hover:bg-[#00227B] rounded-sm px-5 py-2 text-sm font-semibold transition-all duration-200 flex items-center gap-2 disabled:opacity-50 h-9"
            data-testid="generate-invoices-button"
          >
            <Plus className="w-4 h-4" /> {generating ? "Generando..." : "Generar"}
          </button>
          <button
            onClick={() => { setPartialForm({ tenant_id: "", month: genMonth, year: genYear, entry_day: "" }); setPartialOpen(true); }}
            className="bg-white text-gray-900 border border-gray-200 hover:bg-gray-50 rounded-sm px-5 py-2 text-sm font-semibold transition-all duration-200 flex items-center gap-2 h-9"
            data-testid="partial-invoice-button"
          >
            <UserPlus className="w-4 h-4" /> Factura parcial
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 mb-4" data-testid="filter-section">
        <Select value={filterYear} onValueChange={(v) => { setFilterYear(v); setLoading(true); }}>
          <SelectTrigger className="w-28 rounded-sm" data-testid="filter-year-select">
            <SelectValue placeholder="Año" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            {years.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterMonth} onValueChange={(v) => { setFilterMonth(v); setLoading(true); }}>
          <SelectTrigger className="w-36 rounded-sm" data-testid="filter-month-select">
            <SelectValue placeholder="Mes" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            {MONTHS_ES.map((m, i) => <SelectItem key={i+1} value={String(i+1)}>{m}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={(v) => { setFilterStatus(v); setLoading(true); }}>
          <SelectTrigger className="w-32 rounded-sm" data-testid="filter-status-select">
            <SelectValue placeholder="Estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="pendiente">Pendiente</SelectItem>
            <SelectItem value="pagada">Pagada</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-sm" data-testid="invoices-table">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50">
              <TableHead className="text-xs tracking-[0.15em] uppercase text-gray-500 font-bold">N.º</TableHead>
              <TableHead className="text-xs tracking-[0.15em] uppercase text-gray-500 font-bold">Inquilino</TableHead>
              <TableHead className="text-xs tracking-[0.15em] uppercase text-gray-500 font-bold">Plaza</TableHead>
              <TableHead className="text-xs tracking-[0.15em] uppercase text-gray-500 font-bold">Periodo</TableHead>
              <TableHead className="text-xs tracking-[0.15em] uppercase text-gray-500 font-bold text-right">Base</TableHead>
              <TableHead className="text-xs tracking-[0.15em] uppercase text-gray-500 font-bold text-right">IVA</TableHead>
              <TableHead className="text-xs tracking-[0.15em] uppercase text-gray-500 font-bold text-right">Total</TableHead>
              <TableHead className="text-xs tracking-[0.15em] uppercase text-gray-500 font-bold">Estado</TableHead>
              <TableHead className="text-xs tracking-[0.15em] uppercase text-gray-500 font-bold text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {invoices.length === 0 ? (
              <TableRow><TableCell colSpan={9} className="text-center py-8 text-gray-400">No hay facturas</TableCell></TableRow>
            ) : invoices.map((inv) => (
              <TableRow key={inv.id} className="hover:bg-gray-50 transition-colors">
                <TableCell className="text-sm font-medium text-gray-900 py-3 px-4">{inv.invoice_number}</TableCell>
                <TableCell className="text-sm text-gray-900 py-3 px-4">{inv.tenant_name}</TableCell>
                <TableCell className="text-sm text-gray-600 py-3 px-4">{inv.parking_spot_identifier}</TableCell>
                <TableCell className="text-sm text-gray-600 py-3 px-4">
                  {MONTHS_ES[inv.month - 1]} {inv.year}
                  {inv.is_partial && (
                    <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded-sm text-[10px] font-bold bg-violet-50 text-violet-700 border border-violet-200">
                      PARCIAL (dia {inv.entry_day}-{inv.total_days_in_month})
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-sm text-gray-900 py-3 px-4 text-right">{formatCurrency(inv.base_amount)}</TableCell>
                <TableCell className="text-sm text-gray-600 py-3 px-4 text-right">{formatCurrency(inv.vat_amount)}</TableCell>
                <TableCell className="text-sm font-medium text-gray-900 py-3 px-4 text-right">{formatCurrency(inv.total_amount)}</TableCell>
                <TableCell className="py-3 px-4">
                  <button onClick={() => handleToggleStatus(inv)} className="cursor-pointer" data-testid={`toggle-status-${inv.id}`}>
                    <span className={`status-badge ${inv.status === "pagada" ? "status-pagada" : "status-pendiente"}`}>
                      {inv.status === "pagada" ? <CheckCircle className="w-3 h-3 mr-1" /> : <Clock className="w-3 h-3 mr-1" />}
                      {inv.status}
                    </span>
                  </button>
                </TableCell>
                <TableCell className="py-3 px-4 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => handleDownloadPdf(inv.id, inv.invoice_number)} className="p-1.5 text-gray-400 hover:text-[#002FA7] transition-colors" data-testid={`download-pdf-${inv.id}`}>
                      <Download className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDelete(inv)} className="p-1.5 text-gray-400 hover:text-red-600 transition-colors" data-testid={`delete-invoice-${inv.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Partial Invoice Dialog */}
      <Dialog open={partialOpen} onOpenChange={setPartialOpen}>
        <DialogContent className="rounded-sm" data-testid="partial-invoice-dialog">
          <DialogHeader>
            <DialogTitle>Factura parcial (nuevo inquilino)</DialogTitle>
            <DialogDescription>Genera una factura proporcional segun el dia de entrada del inquilino en el mes</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-sm font-semibold text-gray-900 mb-1 block">Inquilino</Label>
              <Select value={partialForm.tenant_id} onValueChange={v => setPartialForm({...partialForm, tenant_id: v})}>
                <SelectTrigger className="rounded-sm" data-testid="partial-tenant-select">
                  <SelectValue placeholder="Selecciona inquilino" />
                </SelectTrigger>
                <SelectContent>
                  {tenants.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-semibold text-gray-900 mb-1 block">Mes</Label>
                <Select value={partialForm.month} onValueChange={v => setPartialForm({...partialForm, month: v})}>
                  <SelectTrigger className="rounded-sm" data-testid="partial-month-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {MONTHS_ES.map((m, i) => <SelectItem key={i+1} value={String(i+1)}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-semibold text-gray-900 mb-1 block">Año</Label>
                <Select value={partialForm.year} onValueChange={v => setPartialForm({...partialForm, year: v})}>
                  <SelectTrigger className="rounded-sm" data-testid="partial-year-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {years.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-sm font-semibold text-gray-900 mb-1 block">Dia de entrada del inquilino</Label>
              <Input
                type="number" min="1" max="31"
                value={partialForm.entry_day}
                onChange={e => setPartialForm({...partialForm, entry_day: e.target.value})}
                placeholder="Ej: 15"
                className="rounded-sm"
                data-testid="partial-entry-day-input"
              />
              <p className="text-xs text-gray-400 mt-1">Se calculara el importe proporcional desde ese dia hasta fin de mes</p>
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setPartialOpen(false)} className="bg-white text-gray-900 border border-gray-200 hover:bg-gray-50 rounded-sm px-4 py-2 text-sm font-semibold transition-all" data-testid="partial-cancel-button">Cancelar</button>
            <button onClick={handleGeneratePartial} disabled={generatingPartial} className="bg-[#002FA7] text-white hover:bg-[#00227B] rounded-sm px-4 py-2 text-sm font-semibold transition-all flex items-center gap-2 disabled:opacity-50" data-testid="partial-generate-button">
              <Plus className="w-4 h-4" /> {generatingPartial ? "Generando..." : "Generar parcial"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
